import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DrawerHeader } from '../../../../../components/Drawer/DrawerHeader';
import InfoCardCompound from '../../../../../components/compound/InfoCardCompound';
import {
    useGetBatchEmployeeExamsQuery,
    useGetBatchEmployeeDocumentsQuery,
    useUploadBatchEmployeeDocumentsSimpleMutation,
    useDeleteBatchEmployeeDocumentMutation
} from '../../../../../services/periodicosApi';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';
import { useState, useRef, useMemo } from 'react';
import { useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import CircularProgress from '@mui/material/CircularProgress';
import { handleErrors } from '../../../../../utils/common';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

type PendingFile = {
    localId: string;
    file: File;
};

interface PeriodicosResultsUploadDrawerProps {
    selectedEmployee?: any;
    selectedBatch?: any;
    handleCloseDrawer: () => void;
    onAfterSave?: () => void;
}

const PeriodicosResultsUploadDrawer = ({
    selectedEmployee,
    selectedBatch,
    handleCloseDrawer,
    onAfterSave
}: PeriodicosResultsUploadDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const dispatch = useAppDispatch();

    const { data: examenesData, isFetching: isFetchingExamenes } = useGetBatchEmployeeExamsQuery(
        selectedEmployee?.idEmpleadoLote,
        { skip: !selectedEmployee?.idEmpleadoLote }
    );

    const { data: documentsData, isFetching: isFetchingDocumentos } = useGetBatchEmployeeDocumentsQuery(
        selectedEmployee?.idEmpleadoLote,
        { skip: !selectedEmployee?.idEmpleadoLote }
    );

    const [uploadDocumentsSimple, { isLoading: isUploadingDocuments }] =
        useUploadBatchEmployeeDocumentsSimpleMutation();
    const [deleteDocument, { isLoading: isDeletingDocument }] =
        useDeleteBatchEmployeeDocumentMutation();

    const examRows: any[] = examenesData || [];

    type DocumentItem = { idDocumento: number; url: string; nombreArchivo: string };

    const rawDocs = Array.isArray(documentsData?.data)
        ? documentsData.data
        : Array.isArray(documentsData)
        ? documentsData
        : [];

    const existingFiles: DocumentItem[] = rawDocs.map((item: any) =>
        typeof item === 'string'
            ? { idDocumento: 0, url: item, nombreArchivo: '' }
            : item
    );

    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): string | null => {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (!ALLOWED_MIME_TYPES.includes(file.type) && !ALLOWED_FILE_EXTENSIONS.includes(ext)) {
            return `Formato no válido. Se permite: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return `El tamaño del archivo no puede superar los ${MAX_FILE_SIZE_MB}MB`;
        }
        return null;
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files || []);
        const newFiles: PendingFile[] = [];
        for (const file of selected) {
            const error = validateFile(file);
            if (error) {
                enqueueSnackbar(`${file.name}: ${error}`, { variant: 'error' });
                continue;
            }
            newFiles.push({ localId: `${Date.now()}-${Math.random()}`, file });
        }
        setPendingFiles((prev) => [...prev, ...newFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemovePendingFile = (localId: string) => {
        setPendingFiles((prev) => prev.filter((f) => f.localId !== localId));
    };

    const handleViewDocument = (url: string) => {
        if (url) {
            window.open(url, '_blank');
        } else {
            enqueueSnackbar('No se pudo obtener el documento.', { variant: 'error' });
        }
    };

    const handleDeleteDocument = async (idDocumento: number) => {
        try {
            const res: any = await deleteDocument({
                idEmpleadoLote: selectedEmployee?.idEmpleadoLote,
                idDocumento
            });
            if (res?.error) {
                handleErrors(res.error);
                return;
            }
            enqueueSnackbar('Documento eliminado correctamente.', { variant: 'success' });
        } catch {
            dispatch(
                showAlert({
                    title: 'No se pudo eliminar el documento',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const getFilenameFromUrl = (url: string): string => {
        try {
            const pathname = new URL(url).pathname;
            return decodeURIComponent(pathname.split('/').pop() || 'Archivo');
        } catch {
            return url.split('/').pop()?.split('?')[0] || 'Archivo';
        }
    };

    const handleConfirmUpload = async () => {
        try {
            const res: any = await uploadDocumentsSimple({
                idEmpleado: selectedEmployee?.idEmpleadoLote,
                files: pendingFiles.map((f) => f.file)
            });
            if (res?.error) {
                handleErrors(res.error);
                return;
            }
            enqueueSnackbar('Documentos cargados correctamente.', { variant: 'success' });
            setPendingFiles([]);
            onAfterSave?.();
            handleCloseDrawer();
        } catch {
            dispatch(
                showAlert({
                    title: 'No se pudo cargar los documentos',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const rawTurno = selectedEmployee?.turnoAsignado || selectedEmployee?.fechaHoraTurno || null;
    const formattedTurno = rawTurno
        ? dayjs(rawTurno).isValid()
            ? dayjs(rawTurno).format('DD/MM/YYYY - HH:mm')
            : rawTurno
        : '-';

    const prestadorName =
        selectedBatch?.prestador?.razonSocial ||
        selectedBatch?.prestador?.nombre ||
        selectedEmployee?.prestador?.razonSocial ||
        selectedEmployee?.prestador?.nombre ||
        '-';

    const infoCardItems = [
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.establishment'),
            value: selectedEmployee?.establecimiento || selectedBatch?.establecimientoNombre || selectedBatch?.establecimiento?.nombre || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.sector'),
            value: selectedEmployee?.sector || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.workPosition'),
            value: selectedEmployee?.puesto || '-'
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.prestador'),
            value: prestadorName
        },
        {
            label: t('drawers.medicalAudit.employeeDetail.columnTitles.appointment'),
            value: formattedTurno
        }
    ];

    const selectedEmployeeName =
        [selectedEmployee?.apellido, selectedEmployee?.nombre]
            .filter((part) => part && part !== '-')
            .join(' ') || '-';
    const selectedEmployeeCuil = selectedEmployee?.cuil || '-';

    const isBusy = isUploadingDocuments || isDeletingDocument;

    const parentTheme = useTheme();
    const scaledTheme = useMemo(
        () =>
            createTheme(parentTheme, {
                typography: {
                    body1: { fontSize: '0.7rem' },
                    body2: { fontSize: '0.62rem' },
                    subtitle1: { fontSize: '0.95rem' },
                    subtitle2: { fontSize: '0.65rem' },
                    caption: { fontSize: '0.72rem' }
                },
                components: {
                    MuiButton: { styleOverrides: { root: { fontSize: '0.68rem' } } },
                    MuiInputBase: { styleOverrides: { root: { fontSize: '0.7rem' } } },
                    MuiMenuItem: { styleOverrides: { root: { fontSize: '0.7rem' } } }
                }
            }),
        [parentTheme]
    );

    return (
        <ThemeProvider theme={scaledTheme}>
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                sx={{ height: '100%' }}
            >
                <Box sx={{ overflow: 'auto', pb: 2 }}>
                    <DrawerHeader onIconClick={handleCloseDrawer} text="Carga de Resultados" />

                    <InfoCardCompound columns={5}>
                        <InfoCardCompound.Avatar />
                        <InfoCardCompound.Title subtitle={`CUIL: ${selectedEmployeeCuil}`}>
                            {selectedEmployeeName}
                        </InfoCardCompound.Title>
                        {infoCardItems.map((item) => (
                            <InfoCardCompound.Item key={item.label} label={item.label}>
                                {item.value}
                            </InfoCardCompound.Item>
                        ))}
                    </InfoCardCompound>

                    {/* Documentación de Resultados */}
                    <Box sx={{ mt: 2 }}>
                        <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <CardHeader
                                sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    bgcolor: 'grey.50',
                                    borderBottom: 1,
                                    borderColor: 'grey.100'
                                }}
                                title={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            color="text.primary"
                                            lineHeight={1.2}
                                        >
                                            Documentación de Resultados
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CloudUploadOutlinedIcon />}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isBusy}
                                        >
                                            Cargar Archivo
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            hidden
                                            multiple
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileInputChange}
                                        />
                                    </Box>
                                }
                            />
                            <CardContent sx={{ p: 2.5 }}>
                                <Grid container spacing={2}>
                                    {existingFiles.map((doc, index) => (
                                        <Grid item xs={12} key={doc.idDocumento || index}>
                                            <Card
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    px: 2,
                                                    py: 1.25,
                                                    bgcolor: 'background.paper',
                                                    border: 1.5,
                                                    borderColor: 'grey.300',
                                                    borderRadius: 2,
                                                    '&:hover': { boxShadow: 2 }
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    fontWeight={500}
                                                    color="text.primary"
                                                    noWrap
                                                    sx={{ flex: 1, mr: 1 }}
                                                >
                                                    {doc.nombreArchivo || getFilenameFromUrl(doc.url)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                    <IconButton
                                                        size="small"
                                                        title="Ver archivo"
                                                        onClick={() => handleViewDocument(doc.url)}
                                                        sx={{ '&:hover': { bgcolor: 'grey.100' } }}
                                                    >
                                                        <VisibilityOutlinedIcon
                                                            sx={{ fontSize: 18, color: 'text.secondary' }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        title="Eliminar archivo"
                                                        onClick={() => handleDeleteDocument(doc.idDocumento)}
                                                        disabled={isDeletingDocument}
                                                        sx={{ '&:hover': { bgcolor: 'error.50' } }}
                                                    >
                                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}

                                    {pendingFiles.map((pending) => (
                                        <Grid item xs={12} key={pending.localId}>
                                            <Card
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    p: 1.5,
                                                    border: '1.5px dashed',
                                                    borderColor: 'primary.light',
                                                    borderRadius: 2,
                                                    bgcolor: 'primary.50'
                                                }}
                                            >
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={500} noWrap>
                                                        {pending.file.name}
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemovePendingFile(pending.localId)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Card>
                                        </Grid>
                                    ))}

                                    {existingFiles.length === 0 && pendingFiles.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="body2"
                                                color="text.disabled"
                                                textAlign="center"
                                                sx={{ py: 2 }}
                                            >
                                                Sin documentos cargados
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Exámenes del empleado (solo lectura) */}
                    <Box sx={{ mt: 2, px: 0.5 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color="text.primary"
                            sx={{ mb: 1 }}
                        >
                            Exámenes del empleado:
                        </Typography>
                        {isFetchingExamenes ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : examRows.length === 0 ? (
                            <Typography variant="body2" color="text.disabled">
                                Sin exámenes definidos
                            </Typography>
                        ) : (
                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                {examRows.map((row: any, idx: number) => (
                                    <Box component="li" key={row?.id ?? idx} sx={{ mb: 0.5 }}>
                                        <Typography variant="body2" color="text.primary">
                                            {row?.examen?.descripcion || `Examen ${row?.id ?? idx + 1}`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>

                <BackdropComponent loading={isFetchingDocumentos || isBusy} />

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseDrawer}
                        disabled={isBusy}
                    >
                        {t('drawer.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirmUpload}
                        disabled={isBusy || pendingFiles.length === 0}
                        sx={{ textTransform: 'none', minWidth: 160 }}
                    >
                        Confirmar carga
                    </Button>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export { PeriodicosResultsUploadDrawer };
